'use client'

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Address } from 'viem'
import { useConfig } from 'wagmi'

import { QUERY_STALE_TIME, USDXL_ADDRESS } from '@/config/consts'
import { isStablecoin } from '@/config/euler-contracts'
import { useAccount } from '@/domain/hooks/useAccount'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { aaveDataLayer } from '@/domain/market-info/aave-data-layer/query'
import { MarketInfo, marketInfoSelectFn } from '@/domain/market-info/marketInfo'
import type { RouteDirection, RouteInfo } from '../components/MarketExplorer'

function getRouteDirection(params: { collateralSymbol?: string; liabilitySymbol?: string }): RouteDirection {
  const { collateralSymbol, liabilitySymbol } = params
  if (!collateralSymbol || !liabilitySymbol) return 'neutral'

  const borrowIsStable = isStablecoin(liabilitySymbol)
  const collateralIsStable = isStablecoin(collateralSymbol)

  if (borrowIsStable && !collateralIsStable) return 'long'
  if (!borrowIsStable && collateralIsStable) return 'short'
  return 'neutral'
}

export interface PooledPositionAsset {
  symbol: string
  amount: number
  amountUsd: number
}

export interface PooledUserPosition {
  supplies: PooledPositionAsset[]
  borrows: PooledPositionAsset[]
}

export interface UsePooledRoutesResult {
  routes: RouteInfo[]
  collateralOptions: Array<{
    underlying: Address
    symbol: string
    name?: string
    logoURI?: string
  }>
  borrowOptions: Array<{
    underlying: Address
    symbol: string
    name?: string
    logoURI?: string
  }>
  isLoading: boolean
  /** User's complete pooled position (all supplies and borrows) */
  userPooledPosition: PooledUserPosition | undefined
}

// Stable empty arrays to avoid recreating on every render
const EMPTY_ROUTES: RouteInfo[] = []
const EMPTY_COLLATERAL_OPTIONS: UsePooledRoutesResult['collateralOptions'] = []
const EMPTY_BORROW_OPTIONS: UsePooledRoutesResult['borrowOptions'] = []

export function usePooledRoutes(): UsePooledRoutesResult {
  const { chainId } = usePageChainId()
  const account = useAccount()
  const wagmiConfig = useConfig()

  // Memoize query options to prevent recreating on every render
  const queryOptions = useMemo(
    () =>
      aaveDataLayer({
        wagmiConfig,
        chainId,
        account,
        refetchEnabled: false, // Don't auto-refetch to avoid loops
      }),
    [wagmiConfig, chainId, account],
  )

  // Memoize the select function
  const selectFn = useMemo(() => marketInfoSelectFn({}), [])

  // Fetch pooled market data using regular useQuery (not suspense)
  const { data: marketData, isLoading } = useQuery({
    ...queryOptions,
    select: selectFn,
    staleTime: QUERY_STALE_TIME,
  }) as { data: MarketInfo | undefined; isLoading: boolean }

  // Extract collateral options (reserves that can be used as collateral)
  const collateralOptions = useMemo(() => {
    if (!marketData) return EMPTY_COLLATERAL_OPTIONS

    return marketData.reserves
      .filter((reserve) => {
        // Can be used as collateral if maxLtv > 0
        return Number(reserve.maxLtv) > 0 && reserve.status !== 'frozen'
      })
      .map((reserve) => ({
        underlying: reserve.token.address as Address,
        symbol: reserve.token.symbol.toString(),
        name: reserve.token.name,
        logoURI: undefined, // Will be resolved by token image logic
      }))
  }, [marketData])

  // Extract borrow options (reserves that can be borrowed)
  const borrowOptions = useMemo(() => {
    if (!marketData) return EMPTY_BORROW_OPTIONS

    return marketData.reserves
      .filter((reserve) => {
        return reserve.borrowEligibilityStatus === 'yes' && reserve.status !== 'frozen'
      })
      .map((reserve) => ({
        underlying: reserve.token.address as Address,
        symbol: reserve.token.symbol.toString(),
        name: reserve.token.name,
        logoURI: undefined,
      }))
  }, [marketData])

  // Get the complete user position (all supplies and borrows)
  const userPooledPosition = useMemo((): PooledUserPosition | undefined => {
    if (!marketData) return undefined

    const supplies: PooledPositionAsset[] = marketData.userPositions
      .filter((pos) => Number(pos.collateralBalance) > 0)
      .map((pos) => ({
        symbol: pos.reserve.token.symbol.toString(),
        amount: Number(pos.collateralBalance),
        amountUsd: Number(pos.collateralBalance) * Number(pos.reserve.priceInUSD),
      }))

    const borrows: PooledPositionAsset[] = marketData.userPositions
      .filter((pos) => Number(pos.borrowBalance) > 0)
      .map((pos) => ({
        symbol: pos.reserve.token.symbol.toString(),
        amount: Number(pos.borrowBalance),
        amountUsd: Number(pos.borrowBalance) * Number(pos.reserve.priceInUSD),
      }))

    if (supplies.length === 0 && borrows.length === 0) return undefined

    return { supplies, borrows }
  }, [marketData])

  // Create routes for each collateral-borrow pair
  const routes = useMemo((): RouteInfo[] => {
    if (!marketData) return EMPTY_ROUTES

    const routeList: RouteInfo[] = []

    // Get collateral reserves (can be used as collateral)
    const collateralReserves = marketData.reserves.filter((r) => Number(r.maxLtv) > 0 && r.status !== 'frozen')

    // Get borrow reserves (can be borrowed)
    const borrowReserves = marketData.reserves.filter(
      (r) => r.borrowEligibilityStatus === 'yes' && r.status !== 'frozen',
    )

    // Pre-index user positions by address for O(1) lookup (perf optimization)
    const positionsByAddress = new Map(
      marketData.userPositions.map((pos) => [pos.reserve.token.address.toLowerCase(), pos]),
    )
    const getUserPositionForReserve = (reserveAddress: string) => positionsByAddress.get(reserveAddress.toLowerCase())

    // Create a route for each valid collateral-borrow pair
    for (const collateral of collateralReserves) {
      for (const borrow of borrowReserves) {
        // Skip if same asset (case-insensitive comparison)
        if (collateral.token.address.toLowerCase() === borrow.token.address.toLowerCase()) continue

        const collateralSymbol = collateral.token.symbol.toString()
        const borrowSymbol = borrow.token.symbol.toString()

        // Calculate max LTV in basis points for consistency with Euler routes
        const maxLtvBps = BigInt(Math.round(Number(collateral.maxLtv) * 10000))

        // For USDXL, use facilitator bucket capacity instead of pool balance
        const isUSDXL = borrow.token.address.toLowerCase() === USDXL_ADDRESS.toLowerCase()
        const availableLiquidity = isUSDXL
          ? Number(marketData.facilitator.availableToBorrow)
          : Number(borrow.availableLiquidity || 0)
        const totalBorrowed = isUSDXL ? Number(marketData.facilitator.currentBorrowed) : Number(borrow.totalDebt || 0)

        const borrowPriceUsd = Number(borrow.priceInUSD)
        const liquidityUSD = availableLiquidity * borrowPriceUsd

        const borrowAPY = borrow.variableBorrowApy ? Number(borrow.variableBorrowApy) * 100 : undefined
        const totalLiquidity = totalBorrowed + availableLiquidity
        const utilization = totalLiquidity > 0 ? (totalBorrowed / totalLiquidity) * 100 : undefined

        const collateralPriceUsd = Number(collateral.priceInUSD)

        // Calculate liquidation LTV in basis points for consistency with Euler routes
        const liquidationLtvBps = BigInt(Math.round(Number(collateral.liquidationThreshold) * 10000))

        // Get user position data for this specific route
        const userCollateralPos = getUserPositionForReserve(collateral.token.address)
        const userBorrowPos = getUserPositionForReserve(borrow.token.address)

        const userCollateralDeposit = userCollateralPos ? Number(userCollateralPos.collateralBalance) : undefined
        const userCollateralDepositUsd =
          userCollateralDeposit && userCollateralDeposit > 0 ? userCollateralDeposit * collateralPriceUsd : undefined
        const userBorrowAmount = userBorrowPos ? Number(userBorrowPos.borrowBalance) : undefined
        const userBorrowAmountUsd =
          userBorrowAmount && userBorrowAmount > 0 ? userBorrowAmount * borrowPriceUsd : undefined

        const DUST_THRESHOLD_USD = 0.01
        const hasCollateral = userCollateralDepositUsd !== undefined && userCollateralDepositUsd >= DUST_THRESHOLD_USD
        const hasDebt = userBorrowAmountUsd !== undefined && userBorrowAmountUsd >= DUST_THRESHOLD_USD
        const hasPosition = hasCollateral || hasDebt

        const pooledHealthFactor =
          hasPosition && marketData.userPositionSummary.healthFactor
            ? Number(marketData.userPositionSummary.healthFactor)
            : undefined

        routeList.push({
          collateral: collateral.token.address as Address,
          liability: borrow.token.address as Address,
          collateralSymbol,
          liabilitySymbol: borrowSymbol,
          collateralTokenInfo: undefined,
          liabilityTokenInfo: undefined,
          borrowAPY,
          maxLTV: maxLtvBps,
          liquidityUSD,
          utilization,
          direction: getRouteDirection({ collateralSymbol, liabilitySymbol: borrowSymbol }),
          hasPosition,
          clusterName: 'HypurrFi Pooled',
          routeType: 'pooled',
          collateralPriceUsd,
          borrowPriceUsd,
          liquidationLTV: liquidationLtvBps,
          userCollateralDeposit,
          userCollateralDepositUsd,
          userBorrowAmount,
          userBorrowAmountUsd,
          healthFactor: pooledHealthFactor,
        })
      }
    }

    return routeList
  }, [marketData])

  return {
    routes,
    collateralOptions,
    borrowOptions,
    isLoading,
    userPooledPosition,
  }
}
